import {
  SpellcasterDTO,
  SoldiersDTO,
  SpellcastersAndSpellsDTO,
  SoldierDTO,
  BuffOrDebuff,
  WarbandMemberDTO,
} from '../types';
import { loadWarbandData, buildHydrationMaps } from './localStorageService';

// eslint-disable-next-line @typescript-eslint/no-var-requires
const getRefMaps = () => {
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const { useReferenceDataStore } = require('../stores/referenceDataStore') as typeof import('../stores/referenceDataStore');
  const s = useReferenceDataStore.getState();
  return buildHydrationMaps({
    weapons: s.weapons,
    armours: s.armours,
    buffs: s.buffs,
    treasureItems: s.treasureItems,
    baseResources: s.allBaseResources,
  });
};

export const playWarbandService = {
  // These getters are used by warbandStore.fetchPlayWarbandData, which is being
  // replaced — keeping stubs to avoid breaking imports during transition.
  getWizard: (_warbandId: string) => Promise.resolve({ data: null as SpellcasterDTO | null }),
  getApprentice: (_warbandId: string) => Promise.resolve({ data: null as SpellcasterDTO | null }),
  getWarbandSpellcasters: (_warbandId: string) => Promise.resolve({ data: null as SpellcastersAndSpellsDTO | null }),
  getSoldiers: (_warbandId: string) => Promise.resolve({ data: { soldiers: [] } as SoldiersDTO }),
  getSoldier: (_warbandId: string, _memberId: string) => Promise.resolve({ data: null as SoldierDTO | null }),

  updateWizardHealth: (_warbandId: string, _health: number) => Promise.resolve({ data: null }),
  updateApprenticeHealth: (_warbandId: string, _health: number) => Promise.resolve({ data: null }),
  updateWizardName: (_warbandId: string, _name: string) => Promise.resolve({ data: null }),
  updateApprenticeName: (_warbandId: string, _name: string) => Promise.resolve({ data: null }),
  updateWizardNotes: (_warbandId: string, _notes: string) => Promise.resolve({ data: null }),
  updateApprenticeNotes: (_warbandId: string, _notes: string) => Promise.resolve({ data: null }),
  updateSoldierHealth: (_warbandId: string, _memberId: string, _health: number) => Promise.resolve({ data: null }),
  updateSoldierName: (_warbandId: string, _memberId: string, _name: string) => Promise.resolve({ data: null }),
  updateSoldierNotes: (_warbandId: string, _memberId: string, _notes: string) => Promise.resolve({ data: null }),
  toggleSoldierLeftTable: (_warbandId: string, _memberId: string) => Promise.resolve({ data: { leftTable: false } }),
  toggleSoldierCarryingTreasure: (_warbandId: string, _memberId: string) => Promise.resolve({ data: { carryingTreasure: false } }),

  getAllAvailableBuffs: (_warbandId: string) => {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const { useReferenceDataStore } = require('../stores/referenceDataStore') as typeof import('../stores/referenceDataStore');
    return Promise.resolve({ data: useReferenceDataStore.getState().buffs as BuffOrDebuff[] });
  },

  getWarbandBuffs: (warbandId: string) => {
    const data = loadWarbandData(warbandId);
    const maps = getRefMaps();
    const countById = new Map<string, number>();
    for (const id of data?.warbandBuffIds ?? []) countById.set(id, (countById.get(id) ?? 0) + 1);
    const buffs = Array.from(countById.entries())
      .map(([id, count]) => {
        const buff = maps.buffMap.get(id);
        return buff ? { ...buff, stackCount: count } : null;
      })
      .filter((b): b is NonNullable<typeof b> => b !== null) as BuffOrDebuff[];
    return Promise.resolve({ data: buffs });
  },

  replaceMemberBuffs: (warbandId: string, memberId: string, buffIds: string[]) => {
    const data = loadWarbandData(warbandId);
    if (!data) return Promise.resolve({ data: null as WarbandMemberDTO | null });
    // Check wizard/apprentice first, then soldiers
    if (data.wizard?.id === memberId) {
      data.wizard = { ...data.wizard, buffIds };
    } else if (data.apprentice?.id === memberId) {
      data.apprentice = { ...data.apprentice, buffIds };
    } else if (data.captain?.id === memberId) {
      data.captain = { ...data.captain, buffIds };
    } else {
      const idx = data.soldiers.findIndex((s) => s.id === memberId);
      if (idx !== -1) data.soldiers[idx] = { ...data.soldiers[idx], buffIds };
    }
    const { saveWarbandData: save } = require('./localStorageService') as typeof import('./localStorageService');
    save(warbandId, data);
    return Promise.resolve({ data: null as WarbandMemberDTO | null });
  },
};
